/**
 * Configurator tier resolution — decides which configuratorDetails entry
 * (local / dev / staging / production) as-configurator's init() should use.
 *
 * Hosted environments follow the deployment:
 *   - onprem            → local
 *   - production host   → production
 *   - "dev" root path   → dev (staging if dev is missing)
 *   - anything else     → staging
 *
 * On localhost, in order:
 *   1. ?server=local|dev|staging|production in the URL forces that tier.
 *   2. The <as-configurator local-server> attribute forces local.
 *   3. Otherwise local is used when configuratorDetails.local exists AND its
 *      APIHub actually answers a probe request. A local entry is often just a
 *      placeholder nothing listens on — probing avoids 404/CORS-failing every
 *      request against a dead server.
 *   4. If local doesn't respond (or has no APIHub of its own): dev, then
 *      staging, then production.
 *
 * Whatever tier wins is written back into the URL as ?server=... so the
 * active choice is visible in the address bar and shareable.
 *
 * Apps commonly only fill in the tiers they actually use. If none of the
 * preferred tiers is defined, fall back across local > dev > staging >
 * production with a warning, so a partial configuratorDetails degrades
 * gracefully instead of throwing.
 */

import { localDev, isProduction, appSuiteRootPath, isOnPrem } from '../../../data/constants.ts';
import { devlog } from '../../../util/utils.ts';
import type { ParamateDetails } from '../../as-app/lib/appConfig.ts';

export type ConfiguratorTier = 'local' | 'dev' | 'staging' | 'production';

export type ConfiguratorDetailsMap = Partial<Record<ConfiguratorTier, ParamateDetails>>;

export interface ResolvedConfiguratorDetails {
  details: ParamateDetails;
  tier: ConfiguratorTier;
}

const TIERS: ConfiguratorTier[] = ['local', 'dev', 'staging', 'production'];

const PROBE_TIMEOUT_MS = 1500;

/** The ?server= URL param, if set to a valid tier name. */
function serverUrlParam(): ConfiguratorTier | null {
  const p = new URLSearchParams(window.location.search).get('server');
  return TIERS.includes(p as ConfiguratorTier) ? (p as ConfiguratorTier) : null;
}

/**
 * Quick reachability probe for a local Paramate server. no-cors keeps a
 * cross-origin response (opaque) from counting as failure — only a network
 * -level error (nothing listening, refused, timeout) rejects. An entry
 * without its own APIHub would fall through to the public default hub, which
 * is not a local server, so treat it as not responding.
 */
async function serverResponds(apiHub: string | undefined): Promise<boolean> {
  if (!apiHub) return false;
  try {
    await fetch(apiHub, { mode: 'no-cors', signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) });
    return true;
  } catch {
    return false;
  }
}

/** Write the resolved tier back into the URL (?server=...) without reloading. */
function syncServerUrlParam(tier: ConfiguratorTier): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('server') === tier) return;
    url.searchParams.set('server', tier);
    history.replaceState(history.state, '', url);
  } catch {
    // URL/history quirks (e.g. sandboxed iframes) must never break init
  }
}

/**
 * Resolve which credentials to use, per the rules in the module doc above.
 * `preferLocalServer` mirrors the <as-configurator local-server> attribute.
 * Returns undefined when the map has no usable entry at all.
 */
export async function resolveConfiguratorDetails(
  cd: ConfiguratorDetailsMap,
  { preferLocalServer = false }: { preferLocalServer?: boolean } = {},
): Promise<ResolvedConfiguratorDetails | undefined> {
  const pick = (tiers: ConfiguratorTier[]): ResolvedConfiguratorDetails | undefined => {
    for (const tier of tiers) {
      const details = cd[tier];
      if (details) return { details, tier };
    }
    return undefined;
  };

  let preference: ConfiguratorTier[];
  if (isOnPrem) {
    preference = ['local'];
  } else if (localDev) {
    const param = serverUrlParam();
    if (param) {
      preference = [param];
    } else if (preferLocalServer) {
      preference = ['local'];
    } else if (cd.local && await serverResponds(cd.local.APIHub)) {
      preference = ['local'];
    } else {
      if (cd.local) devlog('[as-configurator] local Paramate server not responding — using dev credentials');
      preference = ['dev', 'staging', 'production'];
    }
  } else if (isProduction) {
    preference = ['production'];
  } else if (appSuiteRootPath.includes('dev')) {
    preference = ['dev', 'staging'];
  } else {
    preference = ['staging'];
  }

  let resolved = pick(preference);
  // On-prem never falls back to a hosted tier.
  if (!resolved && !isOnPrem) {
    resolved = pick(TIERS);
    if (resolved) {
      console.warn(`[as-configurator] configuratorDetails.${preference[0]} not found for this environment — falling back to "${resolved.tier}" (local > dev > staging > production).`);
    }
  }

  if (resolved && localDev) syncServerUrlParam(resolved.tier);
  if (resolved) devlog(`[as-configurator] using configuratorDetails.${resolved.tier}`);
  return resolved;
}
